import React from 'react';
import { useSegment } from '@sparknz/journeys-common';
import { Button } from '@sparknz/spark-pattern-library';
import {
  LinkRegion,
  trackCustomLink,
  trackLine,
  useTriggerableTrackPageView,
} from '@sparknz/tracking';

import { useProductDetails } from 'journey/productDetails/provider';
import { ProductDetailsActionTypes } from 'journey/productDetails/reducer/productDetails.reducer';
import {
  ActionLayerStatus,
  IfpEligibilityCheckSteps,
  PlanBundleOptions,
} from 'journey/productDetails/typings';
import {
  getProductDetailsPageViewParams,
  trackLinkedProductsEvent,
} from 'journey/productDetails/utils';
import {
  deriveOfferIdFromLineNumber,
  getLineNumber,
  getUnsupportedLinesForChangePlan,
} from 'journey/productDetails/utils/getLineNumber.utils';
import { useQuery } from 'shared/hooks';

const CONTINUE = 'Continue';
const CHANGE_THIS_PLAN = 'Change this plan';
const ACCESSORIES = 'accessories';
interface IfpExistingPlanSelectorButtonProps {
  selectedLineNumber: string;
}

const IfpExistingPlanSelectorButton = ({
  selectedLineNumber,
}: IfpExistingPlanSelectorButtonProps) => {
  const {
    ifpEligibility: {
      eligibility: { eligibleLines, ineligibleLines },
      isChangePlanOfferDetailsLoading,
      isChangePlan,
    },
    dispatch,
    selectedDevice,
    changePlanOffers,
  } = useProductDetails();
  const { isBusiness } = useSegment();
  const { trackPageView } = useTriggerableTrackPageView();
  const { query, replaceQuery } = useQuery();

  const isNewSparkPlan = selectedLineNumber === PlanBundleOptions.ChooseNewPlan;
  const isAccessory = Boolean(
    selectedDevice?.categoryIds?.includes(ACCESSORIES),
  );

  function handleContinueClick() {
    trackCustomLink(CONTINUE);
    const existingOfferId = deriveOfferIdFromLineNumber(selectedLineNumber);

    dispatch({
      type: ProductDetailsActionTypes.SetIfpEligibility,
      ifpEligibility: {
        activeStep: isNewSparkPlan
          ? IfpEligibilityCheckSteps.PlanSelection
          : IfpEligibilityCheckSteps.AddToCart,
        selectedLineNumber:
          getLineNumber(selectedLineNumber) || eligibleLines[0].lineNumber,
        existingOfferId,
        isCheckRequired: true,
        isChangePlan: false,
        isChangePlanOfferDetailsLoading: false,
      },
      planBundleOption: PlanBundleOptions.UseExistingPlan,
      actionLayerStatus: !isNewSparkPlan ? ActionLayerStatus.Close : null,
    });

    replaceQuery({ ...query, planId: existingOfferId });

    trackPageView(
      getProductDetailsPageViewParams({
        groupName: selectedDevice?.groupName,
        primaryCategory: selectedDevice?.primaryCategory,
      }),
    );
    trackLinkedProductsEvent({
      eventInfo: {
        eventName: 'Product View',
        eventAction: 'shop.prodViewAction',
      },
      device: selectedDevice,
      isBusiness,
      isChangePlan,
    });
  }

  function handleChangePlan() {
    const lineNumber = getLineNumber(selectedLineNumber);
    trackCustomLink(CHANGE_THIS_PLAN);
    trackLine(lineNumber);
    dispatch({
      type: ProductDetailsActionTypes.SetIfpEligibility,
      ifpEligibility: {
        activeStep: IfpEligibilityCheckSteps.PlanSelection,
        selectedLineNumber: lineNumber || eligibleLines[0].lineNumber,
        existingOfferId: deriveOfferIdFromLineNumber(selectedLineNumber),
        isChangePlan: true,
      },
      planBundleOption: PlanBundleOptions.UseExistingPlan,
    });
  }

  const hasOnlyOneEligibleLine = eligibleLines.length === 1;
  const lines = [
    ...eligibleLines.map((line) => ({ ...line, isEligible: true })),
    ...ineligibleLines.map((line) => ({ ...line, isEligible: false })),
  ];

  const isEligibleLine =
    lines.filter(
      ({ lineNumber, offerId }) =>
        `${lineNumber}-${offerId}` === selectedLineNumber,
    )[0]?.isEligible || isNewSparkPlan;

  const unSupportedLinesForChangePlan = getUnsupportedLinesForChangePlan({
    ineligibleLines,
    planKey: selectedLineNumber,
  });

  const isLoading = isChangePlanOfferDetailsLoading && !isNewSparkPlan;

  const isDisabled =
    !selectedLineNumber ||
    isNewSparkPlan ||
    unSupportedLinesForChangePlan?.length > 0 ||
    changePlanOffers?.length <= 0;

  return (
    <LinkRegion regionId="productDetails.line.complete">
      <Button
        variant="primary"
        fullWidth
        mb="s2"
        onClick={handleContinueClick}
        isLoading={isLoading}
        disabled={
          (!hasOnlyOneEligibleLine && !selectedLineNumber) || !isEligibleLine
        }
      >
        {CONTINUE}
      </Button>
      {!isAccessory ? (
        <Button
          variant="default"
          fullWidth
          mb="s2"
          onClick={handleChangePlan}
          isLoading={isLoading}
          disabled={isDisabled}
        >
          {CHANGE_THIS_PLAN}
        </Button>
      ) : null}
    </LinkRegion>
  );
};

export default IfpExistingPlanSelectorButton;
